import graphql from "babel-plugin-relay/macro";
import { memo } from "react";
import { useFragment, useMutation } from "react-relay";
import InspectionFormCard, {
  InspectionFormCardPlaceholder,
} from "./InspectionFormCard";

import { InspectionFormOverview$key } from "./__generated__/InspectionFormOverview.graphql";

import styled from "styled-components";
import { InspectionFormOverviewStartMutation } from "./__generated__/InspectionFormOverviewStartMutation.graphql";
import { useNavigate } from "react-router-dom";

export const InspectionPlaceholderCardContainer = memo(function Comp(props: {
  inspectionfield: InspectionFormOverview$key;
  onPlan?: Function;
}) {
  const navigate = useNavigate();
  const data = useFragment(
    graphql`
      fragment InspectionFormOverview on InspectionfieldNode {
        id
        externalId
        year
        state
        plannedDate
        assignedDate
        managementPackageVariant {
          code
          name
          managementPackage {
            name
          }
        }
        latestInspection {
          id
          state
          lastModified
          inspectionform {
            id
            year
          }
        }
        inspectionforms {
          edges {
            node {
              id
              year
            }
          }
        }
        field {
          year
        }
      }
    `,
    props.inspectionfield
  );
  const [startInspection] = useMutation<InspectionFormOverviewStartMutation>(
    graphql`
      mutation InspectionFormOverviewStartMutation(
        $inspectionform: ID!
        $inspectionfield: ID!
      ) {
        startInspection(
          input: {
            inspectionform: $inspectionform
            inspectionfield: $inspectionfield
          }
        ) {
          inspection {
            id
            inspectionfield {
              ...InspectionFormOverview
              ...InspectionDetailsContainer
            }
          }
        }
      }
    `
  );
  if (!data) return null;

  if (!["ASSIGNED", "IN_PROGRESS", "PLANNED"]?.includes(data?.state || "")) {
    return null;
  }

  const inspectionNode = data?.latestInspection;

  const handleClick = (formId?: string) => {
    if (!formId || !data?.id) return;
    if (!data.plannedDate) {
      props.onPlan && props.onPlan();
    } else if (!inspectionNode?.id) {
      startInspection({
        variables: {
          inspectionform: formId,
          inspectionfield: data?.id,
        },
        onCompleted(response) {
          if (!response.startInspection?.inspection?.id) return;
          navigate(`/s/${response.startInspection?.inspection?.id}`);
        },
      });
    } else {
      navigate(`/s/${inspectionNode.id}`);
    }
  };

  if (data.latestInspection && data.latestInspection.inspectionform) {
    return (
      <Container>
        <FormContainerTitle>Schouwformulier</FormContainerTitle>
        <FormsContainer>
          <InspectionFormCard
            key={data.latestInspection.inspectionform.id}
            title={`${data.managementPackageVariant?.code} ${
              data.managementPackageVariant?.managementPackage?.name || ""
            } ${data.managementPackageVariant?.name}`}
            state={
              data.latestInspection.state ||
              (data?.plannedDate ? "PLANNED" : "ASSIGNED")
            }
            year={data.year}
            lastModified={data.latestInspection?.lastModified || undefined}
            assignedDate={data.assignedDate || undefined}
            plannedDate={data.plannedDate || undefined}
            onClick={() =>
              handleClick(data.latestInspection?.inspectionform?.id)
            }
          />
        </FormsContainer>
      </Container>
    );
  }

  const Forms = data?.inspectionforms?.edges
    ?.filter((edge) => edge?.node && edge?.node?.year === data?.year)
    ?.map((formEdge) => {
      const { node: formNode } = formEdge || {};

      return (
        formNode && (
          <InspectionFormCard
            key={formNode?.id || ""}
            title={`${data.managementPackageVariant?.code} ${
              data.managementPackageVariant?.managementPackage?.name || ""
            } ${data.managementPackageVariant?.name}`}
            state={
              inspectionNode?.state ||
              (data?.plannedDate ? "PLANNED" : "ASSIGNED")
            }
            year={formNode?.year}
            lastModified={inspectionNode?.lastModified || undefined}
            assignedDate={data?.assignedDate || undefined}
            plannedDate={data?.plannedDate || undefined}
            onClick={() => handleClick(formNode?.id)}
          />
        )
      );
    });

  return Forms?.length ? (
    <Container>
      <FormContainerTitle>Schouwformulier</FormContainerTitle>
      <FormsContainer>{Forms}</FormsContainer>
    </Container>
  ) : (
    <Container>
      <FormsContainer>
        <InspectionFormCardPlaceholder />
      </FormsContainer>
    </Container>
  );
});

const Container = styled.div`
  height: 300px;
  max-width: 70vw;
  overflow-x: auto;
  @media only screen and (max-width: 768px) {
    max-width: 100vw;
  }
`;

const FormsContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const FormContainerTitle = styled.h3`
  font-family: Roboto;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
`;

export default InspectionPlaceholderCardContainer;
